// convert-doc Web Component - Document conversion with validation
import template from './convert-doc.html';
import styles from './convert-doc.less';
import './doc-editor';
import { apiClient } from '../services/api-client';
import { FileReaderUtil } from '../services/file-reader';
import { SUPPORTED_CONVERSIONS, FRAME_RATES } from '../types/api-types';
export class ConvertDoc extends HTMLElement {
    constructor() {
        super(...arguments);
        this.state = {
            fileName: null,
            fileContent: null,
            sourceFormat: null,
            targetFormat: null,
            validationResult: null,
            conversionResult: null,
            isValidating: false,
            isConverting: false
        };
        this.elements = {};
    }
    connectedCallback() {
        this.render();
        this.cacheElements();
        this.initializeEventListeners();
        this.populateFrameRates();
    }
    render() {
        const styleEl = document.createElement('style');
        styleEl.textContent = styles;
        this.appendChild(styleEl);
        const container = document.createElement('div');
        container.innerHTML = template;
        this.appendChild(container);
    }
    cacheElements() {
        this.elements.uploadArea = this.querySelector('#uploadArea');
        this.elements.fileInput = this.querySelector('#fileInput');
        this.elements.optionsPanel = this.querySelector('#optionsPanel');
        this.elements.currentFile = this.querySelector('#currentFile');
        this.elements.sourceFormat = this.querySelector('#sourceFormat');
        this.elements.targetFormat = this.querySelector('#targetFormat');
        this.elements.frameRate = this.querySelector('#frameRate');
        this.elements.frameRateGroup = this.querySelector('#frameRateGroup');
        this.elements.mode = this.querySelector('#mode');
        this.elements.modeGroup = this.querySelector('#modeGroup');
        this.elements.ignoreFrames = this.querySelector('#ignoreFrames');
        this.elements.validateBtn = this.querySelector('#validateBtn');
        this.elements.convertBtn = this.querySelector('#convertBtn');
        this.elements.discardBtn = this.querySelector('#discardBtn');
        this.elements.downloadBtn = this.querySelector('#downloadBtn');
        this.elements.clearBtn = this.querySelector('#clearBtn');
        this.elements.validationPanel = this.querySelector('#validationPanel');
        this.elements.validationMessages = this.querySelector('#validationMessages');
        this.elements.validationBadges = this.querySelector('#validationBadges');
        this.elements.editorCard = this.querySelector('#editorCard');
        this.elements.resultEditorCard = this.querySelector('#resultEditorCard');
        this.elements.resultPlaceholder = this.querySelector('#resultPlaceholder');
        this.elements.placeholderCard = this.querySelector('#placeholderCard');
        this.elements.docEditor = this.querySelector('#docEditor');
        this.elements.resultEditor = this.querySelector('#resultEditor');
    }
    initializeEventListeners() {
        // Upload area click
        this.elements.uploadArea?.addEventListener('click', () => {
            this.elements.fileInput?.click();
        });
        // File input change
        this.elements.fileInput?.addEventListener('change', (e) => {
            const input = e.target;
            if (input.files && input.files[0]) {
                this.handleFileUpload(input.files[0]);
            }
        });
        // Drag and drop
        this.elements.uploadArea?.addEventListener('dragover', (e) => {
            e.preventDefault();
            this.elements.uploadArea?.classList.add('drag-over');
        });
        this.elements.uploadArea?.addEventListener('dragleave', () => {
            this.elements.uploadArea?.classList.remove('drag-over');
        });
        this.elements.uploadArea?.addEventListener('drop', (e) => {
            e.preventDefault();
            this.elements.uploadArea?.classList.remove('drag-over');
            if (e.dataTransfer?.files && e.dataTransfer.files[0]) {
                this.handleFileUpload(e.dataTransfer.files[0]);
            }
        });
        // Source format change
        this.elements.sourceFormat?.addEventListener('change', () => {
            this.updateTargetFormats();
        });
        // Target format change
        this.elements.targetFormat?.addEventListener('change', () => {
            this.updateOptionsVisibility();
        });
        // Action buttons
        this.elements.validateBtn?.addEventListener('click', () => this.handleValidate());
        this.elements.convertBtn?.addEventListener('click', () => this.handleConvert());
        this.elements.discardBtn?.addEventListener('click', () => this.handleDiscard());
        this.elements.downloadBtn?.addEventListener('click', () => this.handleDownload());
        this.elements.clearBtn?.addEventListener('click', () => this.handleClear());
    }
    populateFrameRates() {
        if (!this.elements.frameRate)
            return;
        FRAME_RATES.forEach(fr => {
            const option = document.createElement('option');
            option.value = fr.value;
            option.textContent = fr.label;
            this.elements.frameRate.appendChild(option);
        });
    }
    async handleFileUpload(file) {
        try {
            // Validate file size
            if (!FileReaderUtil.validateFileSize(file, 50)) {
                alert(`File too large. Maximum size is 50MB. Your file: ${FileReaderUtil.formatFileSize(file.size)}`);
                return;
            }
            // Read file content
            const content = await FileReaderUtil.readAsText(file);
            // Detect format
            const detectedFormat = FileReaderUtil.detectFormat(file.name);
            // Update state
            this.state.fileName = file.name;
            this.state.fileContent = content;
            this.state.sourceFormat = detectedFormat !== 'Unknown' ? detectedFormat : null;
            // Update UI
            this.showOptionsPanel();
            this.elements.currentFile.textContent = file.name;
            if (this.state.sourceFormat && this.elements.sourceFormat) {
                this.elements.sourceFormat.value = this.state.sourceFormat;
            }
            this.updateTargetFormats();
            this.showEditor();
            this.elements.docEditor?.setValue(content);
            // Auto-validate after file upload
            setTimeout(() => this.handleValidate(), 100);
            // Emit event
            this.dispatchEvent(new CustomEvent('file-loaded', {
                detail: { fileName: file.name, format: detectedFormat }
            }));
        }
        catch (error) {
            console.error('File upload error:', error);
            alert(`Failed to read file: ${error}`);
        }
    }
    showOptionsPanel() {
        if (this.elements.uploadArea) {
            this.elements.uploadArea.style.display = 'none';
        }
        if (this.elements.optionsPanel) {
            this.elements.optionsPanel.style.display = 'block';
        }
    }
    showEditor() {
        if (this.elements.placeholderCard) {
            this.elements.placeholderCard.style.display = 'none';
        }
        if (this.elements.editorCard) {
            this.elements.editorCard.style.display = 'block';
        }
        if (this.elements.resultEditorCard) {
            this.elements.resultEditorCard.style.display = 'block';
        }
    }
    showResultEditor() {
        if (this.elements.resultEditorCard) {
            this.elements.resultEditorCard.classList.add('has-result');
        }
    }
    hideResultEditor() {
        if (this.elements.resultEditorCard) {
            this.elements.resultEditorCard.classList.remove('has-result');
            this.elements.resultEditor?.setValue('');
        }
    }
    updateTargetFormats() {
        if (!this.elements.sourceFormat || !this.elements.targetFormat)
            return;
        const sourceFormat = this.elements.sourceFormat.value || this.state.sourceFormat || '';
        const supportedTargets = SUPPORTED_CONVERSIONS[sourceFormat] || [];
        // Clear current options
        this.elements.targetFormat.innerHTML = '<option value="">-- Select Target Format --</option>';
        // Add supported options
        supportedTargets.forEach(format => {
            const option = document.createElement('option');
            option.value = format;
            option.textContent = format;
            this.elements.targetFormat.appendChild(option);
        });
        this.updateOptionsVisibility();
    }
    updateOptionsVisibility() {
        const targetFormat = this.elements.targetFormat?.value || '';
        const needsOptions = targetFormat === 'AvidDS';
        if (this.elements.frameRateGroup) {
            this.elements.frameRateGroup.style.display = needsOptions ? 'block' : 'none';
        }
        if (this.elements.modeGroup) {
            this.elements.modeGroup.style.display = needsOptions ? 'block' : 'none';
        }
    }
    async handleValidate() {
        if (!this.state.fileContent) {
            alert('Please upload a file first');
            return;
        }
        const sourceFormat = this.elements.sourceFormat?.value || this.state.sourceFormat || 'Text';
        this.setButtonLoading(this.elements.validateBtn, true);
        this.state.isValidating = true;
        try {
            let result;
            switch (sourceFormat) {
                case 'SRT':
                    result = await apiClient.validateSRT({
                        content: this.state.fileContent,
                        fileName: this.state.fileName || undefined
                    });
                    break;
                case 'AvidDS':
                    result = await apiClient.validateAvidDS({
                        content: this.state.fileContent,
                        fileName: this.state.fileName || undefined
                    });
                    break;
                case 'Text':
                default:
                    result = await apiClient.validateText({
                        content: this.state.fileContent,
                        fileName: this.state.fileName || undefined,
                        options: {
                            frameRate: this.elements.frameRate?.value,
                            mode: this.elements.mode?.value,
                            ignoreFrames: this.elements.ignoreFrames?.checked
                        }
                    });
                    break;
            }
            this.state.validationResult = result;
            this.displayValidationMessages(result);
            this.elements.docEditor?.highlightMessages(result.messages);
            // Emit event
            this.dispatchEvent(new CustomEvent('validation-complete', {
                detail: { isValid: result.isValid, messages: result.messages }
            }));
        }
        catch (error) {
            console.error('Validation error:', error);
            alert(`Validation failed: ${error}`);
        }
        finally {
            this.setButtonLoading(this.elements.validateBtn, false);
            this.state.isValidating = false;
        }
    }
    async handleConvert() {
        if (!this.state.fileContent || !this.state.fileName) {
            alert('Please upload a file first');
            return;
        }
        const sourceFormat = this.elements.sourceFormat?.value || this.state.sourceFormat || 'Text';
        const targetFormat = this.elements.targetFormat?.value;
        if (!targetFormat) {
            alert('Please select a target format');
            return;
        }
        this.setButtonLoading(this.elements.convertBtn, true);
        this.state.isConverting = true;
        try {
            // Get current content from editor (may have been edited)
            const content = this.elements.docEditor?.getValue() || this.state.fileContent;
            const result = await apiClient.convert({
                content,
                sourceFormat,
                targetFormat,
                fileName: this.state.fileName,
                options: {
                    frameRate: this.elements.frameRate?.value,
                    mode: this.elements.mode?.value,
                    ignoreFrames: this.elements.ignoreFrames?.checked || false
                }
            });
            this.state.conversionResult = result;
            if (result.success) {
                this.displayValidationMessages({ isValid: true, messages: result.messages });
                // Show result in the result editor (not the source editor)
                if (result.outputContent) {
                    this.showResultEditor();
                    this.elements.resultEditor?.setValue(result.outputContent);
                }
                // Show download and discard buttons
                if (this.elements.downloadBtn) {
                    this.elements.downloadBtn.style.display = 'block';
                }
                if (this.elements.discardBtn) {
                    this.elements.discardBtn.style.display = 'block';
                }
                // Emit event
                this.dispatchEvent(new CustomEvent('conversion-complete', {
                    detail: { success: true, result }
                }));
            }
            else {
                this.displayValidationMessages({ isValid: false, messages: result.messages });
            }
        }
        catch (error) {
            console.error('Conversion error:', error);
            alert(`Conversion failed: ${error}`);
        }
        finally {
            this.setButtonLoading(this.elements.convertBtn, false);
            this.state.isConverting = false;
        }
    }
    handleDownload() {
        const result = this.state.conversionResult;
        if (!result || !result.success) {
            alert('No conversion result available');
            return;
        }
        const fileName = result.outputFileName || 'converted-file.txt';
        if (result.outputContent) {
            // Text-based download
            this.downloadTextFile(result.outputContent, fileName);
        }
        else if (result.outputBytes) {
            // Binary download (Excel, etc.)
            this.downloadBinaryFile(result.outputBytes, fileName);
        }
        else {
            alert('No output content available for download');
        }
    }
    handleDiscard() {
        // Clear the conversion result
        this.state.conversionResult = null;
        // Hide the result editor
        this.hideResultEditor();
        // Hide the download and discard buttons
        if (this.elements.downloadBtn) {
            this.elements.downloadBtn.style.display = 'none';
        }
        if (this.elements.discardBtn) {
            this.elements.discardBtn.style.display = 'none';
        }
        // Clear any conversion-related messages
        this.displayValidationMessages({ isValid: true, messages: [] });
    }
    handleClear() {
        // Reset state
        this.state.fileName = null;
        this.state.fileContent = null;
        this.state.sourceFormat = null;
        this.state.targetFormat = null;
        this.state.validationResult = null;
        this.state.conversionResult = null;
        // Reset UI - hide options panel, show upload area
        if (this.elements.uploadArea) {
            this.elements.uploadArea.style.display = 'block';
        }
        if (this.elements.optionsPanel) {
            this.elements.optionsPanel.style.display = 'none';
        }
        // Hide editors and show placeholder
        if (this.elements.editorCard) {
            this.elements.editorCard.style.display = 'none';
        }
        if (this.elements.resultEditorCard) {
            this.elements.resultEditorCard.style.display = 'none';
        }
        if (this.elements.placeholderCard) {
            this.elements.placeholderCard.style.display = 'block';
        }
        // Hide result editor class
        this.hideResultEditor();
        // Hide download and discard buttons
        if (this.elements.downloadBtn) {
            this.elements.downloadBtn.style.display = 'none';
        }
        if (this.elements.discardBtn) {
            this.elements.discardBtn.style.display = 'none';
        }
        // Clear form values
        if (this.elements.sourceFormat) {
            this.elements.sourceFormat.value = '';
        }
        if (this.elements.targetFormat) {
            this.elements.targetFormat.innerHTML = '<option value="">-- Select Target Format --</option>';
        }
        if (this.elements.frameRate) {
            this.elements.frameRate.value = '';
        }
        if (this.elements.mode) {
            this.elements.mode.value = '';
        }
        if (this.elements.ignoreFrames) {
            this.elements.ignoreFrames.checked = false;
        }
        if (this.elements.fileInput) {
            this.elements.fileInput.value = '';
        }
        // Clear editors
        this.elements.docEditor?.setValue('');
        this.elements.resultEditor?.setValue('');
        // Clear validation messages
        this.displayValidationMessages({ isValid: true, messages: [] });
        // Emit event
        this.dispatchEvent(new CustomEvent('cleared'));
    }
    downloadTextFile(content, fileName) {
        const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
        this.triggerDownload(blob, fileName);
    }
    downloadBinaryFile(base64Data, fileName) {
        // Decode base64 to binary
        const binaryString = atob(base64Data);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        // Determine MIME type based on file extension
        const mimeType = this.getMimeType(fileName);
        const blob = new Blob([bytes], { type: mimeType });
        this.triggerDownload(blob, fileName);
    }
    getMimeType(fileName) {
        const ext = fileName.split('.').pop()?.toLowerCase();
        switch (ext) {
            case 'xlsx':
                return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
            case 'xls':
                return 'application/vnd.ms-excel';
            case 'pdf':
                return 'application/pdf';
            case 'zip':
                return 'application/zip';
            default:
                return 'application/octet-stream';
        }
    }
    triggerDownload(blob, fileName) {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }
    displayValidationMessages(result) {
        if (!this.elements.validationMessages || !this.elements.validationBadges)
            return;
        // Helper to check severity (handles both enum and string)
        const isSeverity = (msg, checkSeverity) => {
            return String(msg.severity) === checkSeverity;
        };
        if (result.messages.length === 0) {
            // Show empty state
            this.elements.validationMessages.innerHTML = '<div class="empty-state"><p class="text-muted">No validation messages</p></div>';
            this.elements.validationBadges.innerHTML = '';
            return;
        }
        // Count messages by severity
        const errorCount = result.messages.filter(m => isSeverity(m, 'Error')).length;
        const warningCount = result.messages.filter(m => isSeverity(m, 'Warning')).length;
        const infoCount = result.messages.filter(m => isSeverity(m, 'Info')).length;
        // Update badges
        let badgesHTML = '';
        if (errorCount > 0) {
            badgesHTML += `<span class="badge bg-danger">${errorCount} Error(s)</span>`;
        }
        if (warningCount > 0) {
            badgesHTML += `<span class="badge bg-warning text-dark">${warningCount} Warning(s)</span>`;
        }
        if (infoCount > 0) {
            badgesHTML += `<span class="badge bg-info text-dark">${infoCount} Info</span>`;
        }
        this.elements.validationBadges.innerHTML = badgesHTML;
        // Build messages list
        let html = '<div class="message-list">';
        result.messages.forEach((msg) => {
            const severityStr = String(msg.severity);
            const severityClass = severityStr.toLowerCase();
            const icon = isSeverity(msg, 'Error') ? '❌' :
                isSeverity(msg, 'Warning') ? '⚠️' : 'ℹ️';
            const dataAttr = msg.lineNumber ? `data-line="${msg.lineNumber}"` : '';
            html += `<div class="message-item ${severityClass}" ${dataAttr}>`;
            html += `<div class="message-icon">${icon}</div>`;
            html += '<div class="message-content">';
            html += `<p class="message-text">${msg.message}</p>`;
            html += '<div class="message-meta">';
            if (msg.lineNumber) {
                html += `<span>📍 Line ${msg.lineNumber}</span>`;
            }
            if (msg.context) {
                html += `<span>📝 ${msg.context}</span>`;
            }
            html += '</div></div></div>';
        });
        html += '</div>';
        this.elements.validationMessages.innerHTML = html;
        // Add click handlers to message items for navigation
        this.elements.validationMessages.querySelectorAll('.message-item[data-line]').forEach(item => {
            item.addEventListener('click', () => {
                const lineNumber = parseInt(item.getAttribute('data-line') || '0');
                if (lineNumber > 0) {
                    this.elements.docEditor?.scrollToLine(lineNumber);
                }
            });
        });
    }
    setButtonLoading(button, loading) {
        const spinner = button.querySelector('.spinner-border');
        const text = button.querySelector('.btn-text');
        if (loading) {
            button.disabled = true;
            spinner?.classList.remove('d-none');
            if (text)
                text.textContent = 'Processing...';
        }
        else {
            button.disabled = false;
            spinner?.classList.add('d-none');
            if (text && button === this.elements.validateBtn) {
                text.textContent = 'Validate';
            }
            else if (text && button === this.elements.convertBtn) {
                text.textContent = 'Convert';
            }
        }
    }
}
// Register the custom element
customElements.define('convert-doc', ConvertDoc);
